// TelcoTrack — kompaktní ECharts pie (hotovo vs zbytek plánu) pro KPI karty.

import type { EChartsOption } from 'echarts';
import { echartsEntranceMotion } from './stat-motion';

export interface KpiFractionPieSegmentInput {
  /** Hotovo (čitatel). */
  completed: number;
  /** Celkem (jmenovatel); pie jen když > 0. */
  total: number;
  /** Barva hotové části (accent karty). */
  accentColor: string;
  /** Tmavý dashboard → světlejší „zbytek“. */
  dark: boolean;
  /** Vypnout animaci (prefers-reduced-motion). */
  reduceMotion?: boolean;
}

/** Zbytek plánu (max 0); přeplnění = zbývá 0. */
export function kpiFractionPieRemaining(completed: number, total: number): number {
  const done = Math.max(0, Number(completed) || 0);
  const tot = Math.max(0, Number(total) || 0);
  return Math.max(0, tot - done);
}

/** Pie dává smysl jen u zlomku s kladným jmenovatelem. */
export function kpiFractionPieEligible(completed: number | null | undefined, total: number | null | undefined): boolean {
  if (completed == null || total == null) return false;
  const tot = Number(total);
  return Number.isFinite(tot) && tot > 0;
}

/**
 * Podíl hotovo / celkem v % (zaokrouhleno).
 * Přeplnění plánu → > 100.
 */
export function kpiFractionPiePercent(completed: number, total: number): number {
  const done = Math.max(0, Number(completed) || 0);
  const tot = Math.max(0, Number(total) || 0);
  if (!Number.isFinite(done) || !Number.isFinite(tot) || tot <= 0) return 0;
  return Math.round((done / tot) * 100);
}

/** Text středu donutu — mezera před % (cs). */
export function kpiFractionPiePercentLabel(completed: number, total: number): string {
  return `${kpiFractionPiePercent(completed, total)}\u00A0%`;
}

/**
 * Kompaktní donut: hotovo (accent) + zbytek (ghost).
 * % je HTML overlay ve středu komponenty; bez legendy / tooltipu — zlomek v textu karty.
 */
export function buildKpiFractionPieOption(input: KpiFractionPieSegmentInput): EChartsOption | null {
  if (!kpiFractionPieEligible(input.completed, input.total)) return null;

  const done = Math.max(0, Number(input.completed) || 0);
  const tot = Math.max(0, Number(input.total) || 0);
  const remaining = kpiFractionPieRemaining(done, tot);
  const ghost = input.dark ? 'rgba(148,163,184,0.28)' : 'rgba(100,116,139,0.22)';
  const motion = echartsEntranceMotion(input.reduceMotion);

  // Přeplnění: jedna plná accent výseč (remaining = 0).
  // % label je HTML overlay ve středu donutu (ECharts title na malém canvasu míjí díru).
  const data =
    remaining > 0
      ? [
          { name: 'hotovo', value: Math.min(done, tot), itemStyle: { color: input.accentColor } },
          { name: 'zbytek', value: remaining, itemStyle: { color: ghost } },
        ]
      : [{ name: 'hotovo', value: Math.max(done, tot, 1), itemStyle: { color: input.accentColor } }];

  return {
    ...motion,
    animationThreshold: 0,
    tooltip: { show: false },
    legend: { show: false },
    series: [
      {
        type: 'pie',
        radius: ['60%', '90%'],
        center: ['50%', '50%'],
        silent: true,
        cursor: 'default',
        avoidLabelOverlap: false,
        label: { show: false },
        labelLine: { show: false },
        emphasis: { disabled: true },
        animationType: 'scale',
        animationTypeUpdate: 'expansion',
        animationDuration: motion.animationDuration,
        animationEasing: motion.animationEasing,
        animationDurationUpdate: motion.animationDurationUpdate,
        animationEasingUpdate: motion.animationEasingUpdate,
        data,
      },
    ],
  };
}
